import React, { useEffect } from "react";
import Header from "../../../components/Header";
import Footer from "../../../components/Footer";
import "./RetailEcommerce.css";

import {
  FiArrowRight,
  FiShoppingBag,
  FiMessageSquare,
  FiPackage,
  FiSearch,
  FiTrendingUp,
  FiUsers,
  FiZap,
  FiCheckCircle,
  FiBarChart2,
  FiRefreshCw,
  FiBox,
  FiTarget,
  FiLayers,
  FiClock,
  FiTruck,
  FiCreditCard,
  FiDatabase,
  FiActivity,
} from "react-icons/fi";

const RetailEcommerce = () => {
  useEffect(() => {
    document.title = "AI for Retail & Ecommerce | Dropgear IO";
  }, []);

  return (
    <>
      <Header />

      <main className="retail-page">

        {/* =====================================================
            HERO
        ===================================================== */}

        <section className="retail-hero">

          <div className="retail-hero-grid">

            {/* LEFT */}

            <div className="retail-hero-content">

              <span className="retail-eyebrow">
                RETAIL & ECOMMERCE
              </span>

              <h1>
                AI built for
                <span> commerce that moves.</span>
              </h1>

              <p>
                Dropgear IO helps retail and ecommerce businesses
                use AI across customer interactions, product
                discovery, order workflows, operations, and
                everyday business decisions.
              </p>

              <div className="retail-hero-actions">

                <a
                  href="/contact"
                  className="retail-primary-btn"
                >
                  Talk to Our Team
                  <FiArrowRight />
                </a>

                <a
                  href="#retail-solutions"
                  className="retail-secondary-btn"
                >
                  Explore Solutions
                </a>

              </div>

              <div className="retail-hero-note">

                <FiCheckCircle />

                <span>
                  Designed around practical retail workflows
                </span>

              </div>

            </div>


            {/* RIGHT VISUAL */}

            <div className="retail-hero-visual">

              <div className="retail-visual-glow"></div>

              <div className="retail-dashboard">

                <div className="retail-dashboard-header">

                  <div>

                    <span>
                      RETAIL OPERATIONS
                    </span>

                    <strong>
                      AI Commerce Workspace
                    </strong>

                  </div>

                  <div className="retail-live-status">
                    <i></i>
                    Live
                  </div>

                </div>


                {/* METRICS */}

                <div className="retail-metrics">

                  <div className="retail-metric-card">

                    <span>
                      CUSTOMER REQUESTS
                    </span>

                    <strong>
                      1,284
                    </strong>

                    <small>
                      +18.4%
                    </small>

                  </div>


                  <div className="retail-metric-card">

                    <span>
                      ORDERS
                    </span>

                    <strong>
                      842
                    </strong>

                    <small>
                      +12.7%
                    </small>

                  </div>


                  <div className="retail-metric-card">

                    <span>
                      AI TASKS
                    </span>

                    <strong>
                      376
                    </strong>

                    <small>
                      Active
                    </small>

                  </div>

                </div>


                {/* WORKFLOW */}

                <div className="retail-workflow">

                  <div className="retail-workflow-title">
                    <span>
                      AI WORKFLOW
                    </span>

                    <FiActivity />
                  </div>


                  <div className="retail-workflow-row">

                    <div className="retail-workflow-icon">
                      <FiMessageSquare />
                    </div>

                    <div>
                      <strong>
                        Customer request
                      </strong>

                      <span>
                        Product availability inquiry
                      </span>
                    </div>

                    <FiCheckCircle />

                  </div>


                  <div className="retail-workflow-line"></div>


                  <div className="retail-workflow-row">

                    <div className="retail-workflow-icon">
                      <FiSearch />
                    </div>

                    <div>
                      <strong>
                        Information found
                      </strong>

                      <span>
                        Product and inventory context
                      </span>
                    </div>

                    <FiCheckCircle />

                  </div>


                  <div className="retail-workflow-line"></div>


                  <div className="retail-workflow-row active">

                    <div className="retail-workflow-icon">
                      <FiZap />
                    </div>

                    <div>
                      <strong>
                        Response prepared
                      </strong>

                      <span>
                        Ready for customer
                      </span>
                    </div>

                    <span className="workflow-working">
                      Working
                    </span>

                  </div>

                </div>

              </div>


              <div className="retail-floating-card retail-float-one">

                <FiPackage />

                <span>
                  Order workflow completed
                </span>

              </div>


              <div className="retail-floating-card retail-float-two">

                <FiTrendingUp />

                <span>
                  Sales signal detected
                </span>

              </div>

            </div>

          </div>

        </section>


        {/* =====================================================
            INTRO
        ===================================================== */}

        <section className="retail-intro">

          <div className="retail-container">

            <div className="retail-intro-grid">

              <div>

                <span className="retail-section-label">
                  THE MODERN RETAIL CHALLENGE
                </span>

                <h2>
                  Commerce generates
                  <strong> endless work.</strong>
                </h2>

              </div>


              <div>

                <p>
                  Retail teams constantly work across customers,
                  products, orders, inventory, suppliers, and
                  business data. Dropgear IO brings AI into these
                  workflows to help teams handle repetitive work
                  and make information easier to act on.
                </p>

                <p>
                  From the first customer question to post-purchase
                  operations, AI can become part of the workflow
                  instead of sitting separately from it.
                </p>

              </div>

            </div>

          </div>

        </section>


        {/* =====================================================
            SOLUTIONS
        ===================================================== */}

        <section
          id="retail-solutions"
          className="retail-solutions"
        >

          <div className="retail-container">

            <div className="retail-section-heading">

              <span>
                AI FOR RETAIL & ECOMMERCE
              </span>

              <h2>
                Turn retail workflows
                <br />
                into <strong>intelligent workflows.</strong>
              </h2>

              <p>
                Apply AI where your teams spend time every day —
                customer support, product operations, orders,
                inventory, analytics, and more.
              </p>

            </div>


            <div className="retail-solutions-grid">

              {/* CARD 01 */}

              <div className="retail-solution-card featured">

                <div className="retail-solution-icon">
                  <FiMessageSquare />
                </div>

                <span>
                  01
                </span>

                <h3>
                  AI Customer Assistance
                </h3>

                <p>
                  Help customers find information, understand
                  products, track requests, and navigate common
                  questions through AI-powered experiences.
                </p>

                <ul>

                  <li>
                    <FiCheckCircle />
                    Product questions
                  </li>

                  <li>
                    <FiCheckCircle />
                    Customer assistance
                  </li>

                  <li>
                    <FiCheckCircle />
                    Order information
                  </li>

                </ul>

              </div>


              {/* CARD 02 */}

              <div className="retail-solution-card">

                <div className="retail-solution-icon">
                  <FiSearch />
                </div>

                <span>
                  02
                </span>

                <h3>
                  Product Discovery
                </h3>

                <p>
                  Make it easier for customers to discover products
                  through conversational search and intelligent
                  product information.
                </p>

                <ul>

                  <li>
                    <FiCheckCircle />
                    Natural language search
                  </li>

                  <li>
                    <FiCheckCircle />
                    Product recommendations
                  </li>

                  <li>
                    <FiCheckCircle />
                    Product comparison
                  </li>

                </ul>

              </div>


              {/* CARD 03 */}

              <div className="retail-solution-card">

                <div className="retail-solution-icon">
                  <FiPackage />
                </div>

                <span>
                  03
                </span>

                <h3>
                  Order Operations
                </h3>

                <p>
                  Support order-related workflows by connecting
                  information across orders, customers, and
                  operational processes.
                </p>

                <ul>

                  <li>
                    <FiCheckCircle />
                    Order status
                  </li>

                  <li>
                    <FiCheckCircle />
                    Request handling
                  </li>

                  <li>
                    <FiCheckCircle />
                    Workflow assistance
                  </li>

                </ul>

              </div>


              {/* CARD 04 */}

              <div className="retail-solution-card">

                <div className="retail-solution-icon">
                  <FiBox />
                </div>

                <span>
                  04
                </span>

                <h3>
                  Inventory Intelligence
                </h3>

                <p>
                  Bring AI into inventory-related workflows to help
                  teams understand product availability and
                  operational information.
                </p>

                <ul>

                  <li>
                    <FiCheckCircle />
                    Inventory information
                  </li>

                  <li>
                    <FiCheckCircle />
                    Stock signals
                  </li>

                  <li>
                    <FiCheckCircle />
                    Product availability
                  </li>

                </ul>

              </div>


              {/* CARD 05 */}

              <div className="retail-solution-card">

                <div className="retail-solution-icon">
                  <FiBarChart2 />
                </div>

                <span>
                  05
                </span>

                <h3>
                  Retail Analytics
                </h3>

                <p>
                  Turn business data into easier-to-understand
                  insights that help teams identify trends and
                  investigate operational questions.
                </p>

                <ul>

                  <li>
                    <FiCheckCircle />
                    Business insights
                  </li>

                  <li>
                    <FiCheckCircle />
                    Trend discovery
                  </li>

                  <li>
                    <FiCheckCircle />
                    Data exploration
                  </li>

                </ul>

              </div>


              {/* CARD 06 */}

              <div className="retail-solution-card">

                <div className="retail-solution-icon">
                  <FiRefreshCw />
                </div>

                <span>
                  06
                </span>

                <h3>
                  Workflow Automation
                </h3>

                <p>
                  Connect AI with repetitive operational processes
                  to reduce manual handling and help teams move
                  work forward.
                </p>

                <ul>

                  <li>
                    <FiCheckCircle />
                    Repetitive tasks
                  </li>

                  <li>
                    <FiCheckCircle />
                    Workflow routing
                  </li>

                  <li>
                    <FiCheckCircle />
                    Process assistance
                  </li>

                </ul>

              </div>

            </div>

          </div>

        </section>


        {/* =====================================================
            CUSTOMER EXPERIENCE
        ===================================================== */}

        <section className="retail-customer">

          <div className="retail-container">

            <div className="retail-two-column">

              <div className="retail-feature-visual">

                <div className="retail-chat-window">

                  <div className="retail-chat-header">

                    <div className="retail-agent-avatar">
                      <FiZap />
                    </div>

                    <div>

                      <strong>
                        Dropgear IO AI
                      </strong>

                      <span>
                        Shopping Assistant
                      </span>

                    </div>

                    <span className="retail-chat-online">
                      ●
                    </span>

                  </div>


                  <div className="retail-chat-body">

                    <div className="retail-message customer">
                      I need a lightweight laptop for
                      business travel.
                    </div>

                    <div className="retail-message ai">
                      I can help with that. I found several
                      options based on portability, performance,
                      and your business use case.
                    </div>

                    <div className="retail-product-mini">

                      <div className="retail-product-image">
                        <FiBox />
                      </div>

                      <div>

                        <strong>
                          Business Laptop
                        </strong>

                        <span>
                          Lightweight · 16GB RAM
                        </span>

                      </div>

                      <FiArrowRight />

                    </div>

                  </div>

                </div>

              </div>


              <div className="retail-feature-content">

                <span className="retail-section-label">
                  CUSTOMER EXPERIENCE
                </span>

                <h2>
                  Make every
                  <strong> interaction more useful.</strong>
                </h2>

                <p>
                  Customers expect quick access to product and
                  order information. AI can help businesses create
                  more useful customer experiences without requiring
                  every interaction to be handled manually.
                </p>


                <div className="retail-check-list">

                  <div>

                    <FiCheckCircle />

                    <span>
                      Conversational product discovery
                    </span>

                  </div>

                  <div>

                    <FiCheckCircle />

                    <span>
                      Product information assistance
                    </span>

                  </div>

                  <div>

                    <FiCheckCircle />

                    <span>
                      Order and delivery questions
                    </span>

                  </div>

                  <div>

                    <FiCheckCircle />

                    <span>
                      Support workflow assistance
                    </span>

                  </div>

                </div>

              </div>

            </div>

          </div>

        </section>


        {/* =====================================================
            OPERATIONS
        ===================================================== */}

        <section className="retail-operations">

          <div className="retail-container">

            <div className="retail-section-heading centered">

              <span>
                RETAIL OPERATIONS
              </span>

              <h2>
                Less repetitive work.
                <br />
                <strong>More operational focus.</strong>
              </h2>

              <p>
                AI can support teams behind the storefront by
                helping process information, route tasks, and
                surface useful operational context.
              </p>

            </div>


            <div className="retail-operations-grid">

              <div className="retail-operation-card">

                <div className="retail-operation-icon">
                  <FiClock />
                </div>

                <h3>
                  Repetitive Requests
                </h3>

                <p>
                  Reduce manual handling of recurring information
                  requests and routine operational tasks.
                </p>

              </div>


              <div className="retail-operation-card">

                <div className="retail-operation-icon">
                  <FiDatabase />
                </div>

                <h3>
                  Information Access
                </h3>

                <p>
                  Help teams find relevant product, order, and
                  operational information more efficiently.
                </p>

              </div>


              <div className="retail-operation-card">

                <div className="retail-operation-icon">
                  <FiLayers />
                </div>

                <h3>
                  Workflow Coordination
                </h3>

                <p>
                  Connect multiple steps and systems into clearer
                  workflows supported by AI.
                </p>

              </div>


              <div className="retail-operation-card">

                <div className="retail-operation-icon">
                  <FiTarget />
                </div>

                <h3>
                  Decision Support
                </h3>

                <p>
                  Help teams investigate operational questions
                  and understand available business information.
                </p>

              </div>

            </div>

          </div>

        </section>


        {/* =====================================================
            USE CASES
        ===================================================== */}

        <section className="retail-usecases">

          <div className="retail-container">

            <div className="retail-section-heading">

              <span>
                PRACTICAL USE CASES
              </span>

              <h2>
                Start with one workflow.
                <br />
                <strong>Expand from there.</strong>
              </h2>

              <p>
                AI adoption does not need to start with everything.
                Identify a high-value workflow and build from a
                practical starting point.
              </p>

            </div>


            <div className="retail-usecase-list">

              <div className="retail-usecase">

                <span>
                  01
                </span>

                <FiMessageSquare />

                <div>

                  <h3>
                    Customer Support Assistant
                  </h3>

                  <p>
                    Help customers with common questions,
                    product information, and order-related
                    requests.
                  </p>

                </div>

                <FiArrowRight />

              </div>


              <div className="retail-usecase">

                <span>
                  02
                </span>

                <FiSearch />

                <div>

                  <h3>
                    Intelligent Product Search
                  </h3>

                  <p>
                    Let customers search for products using
                    natural language instead of rigid filters.
                  </p>

                </div>

                <FiArrowRight />

              </div>


              <div className="retail-usecase">

                <span>
                  03
                </span>

                <FiTruck />

                <div>

                  <h3>
                    Order Operations Assistant
                  </h3>

                  <p>
                    Support teams handling order, shipping,
                    and delivery-related workflows.
                  </p>

                </div>

                <FiArrowRight />

              </div>


              <div className="retail-usecase">

                <span>
                  04
                </span>

                <FiBarChart2 />

                <div>

                  <h3>
                    Business Intelligence Assistant
                  </h3>

                  <p>
                    Help teams explore business information
                    and investigate operational questions.
                  </p>

                </div>

                <FiArrowRight />

              </div>

            </div>

          </div>

        </section>


        {/* =====================================================
            WHY DROPGEAR
        ===================================================== */}

        <section className="retail-why">

          <div className="retail-container">

            <div className="retail-why-grid">

              <div>

                <span className="retail-section-label">
                  WHY DROPGEAR IO
                </span>

                <h2>
                  AI that fits
                  <strong> your commerce workflow.</strong>
                </h2>

                <p>
                  Retail businesses do not need AI for the sake
                  of AI. They need practical systems that connect
                  to the work already happening across customers,
                  products, orders, and operations.
                </p>

                <a
                  href="/contact"
                  className="retail-primary-btn"
                >
                  Discuss Your Business
                  <FiArrowRight />
                </a>

              </div>


              <div className="retail-why-list">

                <div>

                  <FiTarget />

                  <section>

                    <h3>
                      Workflow-focused
                    </h3>

                    <p>
                      Start with a real business process rather
                      than a generic AI implementation.
                    </p>

                  </section>

                </div>


                <div>

                  <FiLayers />

                  <section>

                    <h3>
                      Flexible AI systems
                    </h3>

                    <p>
                      Combine AI models, automation, agents,
                      information, and business logic where useful.
                    </p>

                  </section>

                </div>


                <div>

                  <FiZap />

                  <section>

                    <h3>
                      Built for practical use
                    </h3>

                    <p>
                      Focus on measurable workflows and useful
                      business outcomes.
                    </p>

                  </section>

                </div>

              </div>

            </div>

          </div>

        </section>


        {/* =====================================================
            CTA
        ===================================================== */}

        <section className="retail-final-cta">

          <div className="retail-final-glow"></div>

          <div className="retail-final-content">

            <span>
              RETAIL & ECOMMERCE
            </span>

            <h2>
              Ready to put AI
              <br />
              to work in commerce?
            </h2>

            <p>
              Tell Dropgear IO about a customer experience,
              operational workflow, or business process you
              want to improve with AI.
            </p>

            <a
              href="/contact"
              className="retail-cta-button"
            >
              Start a Conversation
              <FiArrowRight />
            </a>

          </div>

        </section>

      </main>

      <Footer />
    </>
  );
};

export default RetailEcommerce;